class VelmorCartDrawer {
  constructor() {
    this.drawer = document.querySelector('.js-cart-drawer');
    this.overlay = document.querySelector('.js-cart-overlay');
    this.isOpen = false;

    if (this.drawer && this.overlay) {
      this.initEventListeners();
      this.fetchCart();
    }
  }

  initEventListeners() {
    // Open cart via header icon or any link to /cart
    document.addEventListener('click', (e) => {
      const cartLink = e.target.closest('a[href^="/cart"]');
      if (cartLink && !cartLink.classList.contains('btn-view-cart') && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        this.open();
      }
    });

    // Overlay close
    if (this.overlay) this.overlay.addEventListener('click', () => this.close());
    
    // ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) this.close();
    });

    // Intercept Add to Cart Forms
    document.addEventListener('submit', (e) => {
      const form = e.target;
      if (form.action && form.action.includes('/cart/add')) {
        e.preventDefault();
        this.addToCart(new FormData(form), form);
      }
    });
    
    // Bind initial inner elements
    this.rebindEvents();
  }

  rebindEvents() {
    this.closeBtn = this.drawer.querySelector('.js-cart-close');
    this.discountInput = this.drawer.querySelector('.js-cart-discount-input');
    this.discountApplyBtn = this.drawer.querySelector('.js-cart-discount-apply');
    this.discountRemoveBtns = this.drawer.querySelectorAll('.js-cart-discount-remove');
    this.discountMessage = this.drawer.querySelector('.js-cart-discount-message');
    this.checkoutBtn = this.drawer.querySelector('.js-cart-checkout');

    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.close());
    }

    if (this.discountApplyBtn) {
      this.discountApplyBtn.addEventListener('click', () => this.applyDiscount());
    }

    if (this.discountRemoveBtns) {
      this.discountRemoveBtns.forEach(btn => {
        btn.addEventListener('click', (e) => {
          this.removeDiscount();
        });
      });
    }

    if (this.checkoutBtn) {
      this.checkoutBtn.addEventListener('click', (e) => {
        // Redirection with discount is no longer needed since it's applied via API
      });
    }
  }

  open() {
    this.isOpen = true;
    this.overlay.classList.add('is-active');
    this.drawer.classList.add('is-active');
    document.body.style.overflow = 'hidden';
    this.fetchCart(); // refresh data on open to get latest discounts
  }

  close() {
    this.isOpen = false;
    this.overlay.classList.remove('is-active');
    this.drawer.classList.remove('is-active');
    document.body.style.overflow = '';
  }

  async fetchCart() {
    try {
      this.drawer.classList.add('is-loading');
      const response = await fetch(`${window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/' }?section_id=velmor-cart-drawer`);
      const text = await response.text();
      this.renderSection(text);
      
      const countEl = this.drawer.querySelector('.js-cart-count-data');
      if (countEl) {
        this.updateCartCount(parseInt(countEl.innerText) || 0);
      }
    } catch (error) {
      console.error('Error fetching cart:', error);
    } finally {
      this.drawer.classList.remove('is-loading');
    }
  }

  async addToCart(formData, formElement) {
    try {
      // Disable button
      const submitBtn = formElement.querySelector('[type="submit"], [name="add"]');
      let originalText = '';
      if (submitBtn) {
        originalText = submitBtn.innerHTML;
        submitBtn.innerHTML = 'Añadiendo...';
        submitBtn.disabled = true;
      }

      const response = await fetch(`${window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/' }cart/add.js`, {
        method: 'POST',
        body: formData
      });
      
      if (submitBtn) {
        submitBtn.innerHTML = originalText;
        submitBtn.disabled = false;
      }

      if (response.ok) {
        this.open();
      } else {
        const errorData = await response.json();
        alert(errorData.description || 'Error añadiendo al carrito');
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
    }
  }

  async updateQuantity(line, quantity) {
    try {
      this.drawer.classList.add('is-loading');
      const response = await fetch(`${window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/' }cart/change.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ 
          line: line, 
          quantity: quantity,
          sections: 'velmor-cart-drawer'
        })
      });
      const data = await response.json();
      
      if (data.sections && data.sections['velmor-cart-drawer']) {
        this.renderSection(data.sections['velmor-cart-drawer']);
      }
      this.updateCartCount(data.item_count);
    } catch (error) {
      console.error('Error updating quantity:', error);
    } finally {
      this.drawer.classList.remove('is-loading');
    }
  }

  renderSection(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    const newInner = doc.querySelector('#cart-drawer-inner');
    const oldInner = this.drawer.querySelector('#cart-drawer-inner');
    if (newInner && oldInner) {
      oldInner.innerHTML = newInner.innerHTML;
      this.rebindEvents();
    }
  }

  updateCartCount(count) {
    const countEls = document.querySelectorAll('.cart-count');
    countEls.forEach(el => {
      if (count > 0) {
        el.innerText = count;
        el.style.display = 'flex';
      } else {
        el.style.display = 'none';
      }
    });
  }

  async applyDiscount() {
    const code = this.discountInput ? this.discountInput.value.trim() : '';
    if (code) {
      try {
        this.drawer.classList.add('is-loading');
        
        // Use cart update API to apply discount
        const response = await fetch(`${window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/' }cart/update.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ 
            discount: code,
            sections: 'velmor-cart-drawer'
          })
        });
        
        const data = await response.json();
        
        if (data.sections && data.sections['velmor-cart-drawer']) {
          this.renderSection(data.sections['velmor-cart-drawer']);
          
          // Re-select message element after render
          this.discountMessage = this.drawer.querySelector('.js-cart-discount-message');
          
          // Check if discount was actually applied (total_discount > 0 or it's in the cart)
          // Since data.total_discount might be 0 even if a code is valid (e.g. for free shipping),
          // We can check if the HTML contains the discount title, or if we have it in cart data.
          // Wait, Shopify API sometimes ignores invalid codes entirely or sets them but doesn't apply.
          // We can just fetch /cart.js to verify if needed, or just let liquid show it.
          // If liquid shows it, we can display success.
          
          // Actually, let's just show error if it's NOT applied. 
          // We can look for the applied code in the DOM.
          const appliedDiscounts = this.drawer.querySelectorAll('.cart-drawer__discount-row .js-discount-title');
          let codeApplied = false;
          appliedDiscounts.forEach(el => {
            if (el.textContent.trim().toUpperCase() === code.toUpperCase()) {
              codeApplied = true;
            }
          });
          
          if (!codeApplied && this.discountMessage) {
            // It might be a free shipping discount or item discount.
            // Let's check item discounts as well
            const itemDiscounts = this.drawer.querySelectorAll('.cart-drawer__line-discount .js-discount-title');
            itemDiscounts.forEach(el => {
              if (el.textContent.trim().toUpperCase() === code.toUpperCase()) {
                codeApplied = true;
              }
            });
          }

          if (this.discountMessage) {
            if (codeApplied) {
              const appliedText = document.querySelector('.js-cart-drawer').getAttribute('data-applied-text') || 'Código de descuento [code] aplicado';
              this.discountMessage.textContent = appliedText.replace('[code]', code);
              this.discountMessage.className = 'discount-message success';
            } else {
              const invalidText = document.querySelector('.js-cart-drawer').getAttribute('data-invalid-text') || 'Código no válido';
              this.discountMessage.textContent = invalidText;
              this.discountMessage.className = 'discount-message error';
              
              // Clear the discount from session so it doesn't get stuck
              fetch(`${window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/' }cart/update.js`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ discount: '' })
              });
            }
          }
        }
      } catch (error) {
        console.error('Error applying discount:', error);
      } finally {
        this.drawer.classList.remove('is-loading');
      }
    } else {
      if (this.discountMessage) {
        this.discountMessage.textContent = '';
        this.discountMessage.className = 'discount-message';
      }
    }
  }

  async removeDiscount() {
    try {
      this.drawer.classList.add('is-loading');
      const response = await fetch(`${window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/' }cart/update.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ 
          discount: '',
          sections: 'velmor-cart-drawer'
        })
      });
      
      const data = await response.json();
      if (data.sections && data.sections['velmor-cart-drawer']) {
        this.renderSection(data.sections['velmor-cart-drawer']);
        
        if (this.discountMessage) {
          this.discountMessage.textContent = '';
          this.discountMessage.className = 'discount-message';
        }
      }
    } catch (error) {
      console.error('Error removing discount:', error);
    } finally {
      this.drawer.classList.remove('is-loading');
    }
  }

  formatMoney(cents) {
    const formatter = new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    });
    return formatter.format(cents / 100);
  }
}

// Initialize on load
let velmorCart;
document.addEventListener("DOMContentLoaded", () => {
  velmorCart = new VelmorCartDrawer();
  window.velmorCart = velmorCart; // Expose to global for onclick handlers
});
